/**
 * An in-memory `NotificationsSource` — what `/__ui/notifications` and every unit test run
 * against, with no server and no database.
 *
 * The rows reproduce the ones captured on monday (`discovery/ui-reference/notifications/bell.png`):
 * an API notification, an automation notice, an assignment and a subscribed-item update, so a
 * side-by-side has something real to compare.
 */
import { NOTIFICATION_KINDS } from '@elizmonday/shared';
import type {
  BoardMuteView,
  InboxFacets,
  InboxRowView,
  NotificationRowView,
  NotificationsSource,
  SettingsRowView,
} from './types';

const minutesAgo = (minutes: number): string => new Date(Date.now() - minutes * 60_000).toISOString();
/** One hour before local midnight: always in the panel's "Yesterday" group, whatever the time of day is now. */
const yesterdayEvening = (): string => {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  return new Date(startOfToday.getTime() - 60 * 60_000).toISOString();
};

export function fixtureNotifications(): NotificationRowView[] {
  return [
    {
      id: '1000000000004',
      kind: 'mention',
      kindName: 'mention',
      title: 'Antonino mentioned you in an update',
      text: 'Can you take a look at the Shopify sync before Friday?',
      read: false,
      createdAt: minutesAgo(2),
      actor: { id: '3247457', name: 'Antonino', photoUrl: null },
      boardId: '1000000000000',
      boardName: 'Sandbox – Column types',
      itemId: '1000000000010',
      itemName: 'Item 25 · LIVE CHECK',
      updateId: '1000000000100',
    },
    {
      id: '1000000000003',
      kind: 'api',
      kindName: null,
      title: 'Roberto',
      text: 'M5 probe: notification on a sandbox item',
      read: false,
      createdAt: minutesAgo(9),
      actor: { id: '3733063', name: 'Roberto', photoUrl: null },
      boardId: '1000000000000',
      boardName: 'Sandbox – Column types',
      itemId: '1000000000010',
      itemName: 'Item 25 · LIVE CHECK',
      updateId: null,
    },
    {
      id: '1000000000002',
      kind: 'automation_system',
      kindName: 'automation_system_notify',
      title: 'Your automation was disabled',
      text: 'Your automation was deactivated due to a missing field.',
      read: false,
      createdAt: yesterdayEvening(),
      actor: null,
      boardId: '1000000000000',
      boardName: 'Sandbox – Column types',
      itemId: null,
      itemName: null,
      updateId: null,
    },
    {
      id: '1000000000001',
      kind: 'assign',
      kindName: 'board_assigned_in_column',
      title: 'Antonino assigned you to the item "update SKU for Boolby paid version"',
      text: null,
      read: true,
      createdAt: minutesAgo(60 * 24 * 30),
      actor: { id: '3247457', name: 'Antonino', photoUrl: null },
      boardId: '1000000000000',
      boardName: 'MK-Store-Updates',
      itemId: '1000000000011',
      itemName: 'update SKU for Boolby paid version',
      updateId: null,
    },
  ];
}

export function fixtureSettings(): SettingsRowView[] {
  return NOTIFICATION_KINDS.map((kind) => ({
    kind: kind.kind,
    kindName: kind.kindName,
    description: kind.description,
    section: kind.section,
    isForAdminsOnly: kind.isForAdminsOnly,
    isForNonGuestsOnly: kind.isForNonGuestsOnly,
    supported: kind.supported,
    channels: (['Monday', 'Email', 'Slack'] as const).map((channel) => ({
      name: channel,
      enabled: kind.channels[channel].enabled,
      editableStatus: kind.channels[channel].editable,
    })),
  }));
}

export function fixtureInbox(): InboxRowView[] {
  return [
    {
      id: '1000000000100',
      boardId: '1000000000000',
      boardName: 'PRJ-2024-PLAN-Projects [Main]',
      groupId: 'q3',
      groupTitle: 'Q3',
      itemId: '1000000000010',
      itemName: 'Research project about AutoCM',
      bodyHtml: '<p>Created the Shopify sync system for AutoCM and prepared GUI for stores connection.</p>',
      bodyText: 'Created the Shopify sync system for AutoCM and prepared GUI for stores connection.',
      createdAt: minutesAgo(90),
      read: false,
      creator: { id: '3733063', name: 'Roberto', photoUrl: null },
      replyCount: 1,
      likeCount: 2,
    },
    {
      id: '1000000000101',
      boardId: '1000000000001',
      boardName: 'MK-Store-Updates',
      groupId: 'scheduled',
      groupTitle: '⌨️ Scheduled / In progress',
      itemId: '1000000000012',
      itemName: 'BEA-Homepage Update',
      bodyHtml: '<p>We had some troubles to start integration, we will do ASAP</p>',
      bodyText: 'We had some troubles to start integration, we will do ASAP',
      createdAt: minutesAgo(60 * 30),
      read: true,
      creator: { id: '3247457', name: 'Antonino', photoUrl: null },
      replyCount: 0,
      likeCount: 0,
    },
  ];
}

/** A source backed by mutable arrays. Every write is applied locally, like the server's. */
export function createFixtureSource(seed?: {
  notifications?: NotificationRowView[];
  inbox?: InboxRowView[];
  mutes?: BoardMuteView[];
}): NotificationsSource {
  let rows = seed?.notifications ?? fixtureNotifications();
  let inbox = seed?.inbox ?? fixtureInbox();
  let settings = fixtureSettings();
  let mutes = seed?.mutes ?? [];

  const unread = (): number => rows.filter((row) => !row.read).length;

  return {
    async list(input) {
      let page = rows;
      if (input.tab === 'mentioned') page = page.filter((row) => row.kind === 'mention');
      if (input.tab === 'assigned') page = page.filter((row) => row.kind === 'assign');
      if (input.unreadOnly) page = page.filter((row) => !row.read);
      if (input.query) {
        const needle = input.query.toLowerCase();
        page = page.filter((row) =>
          [row.title, row.text, row.boardName, row.itemName]
            .filter(Boolean)
            .some((value) => (value as string).toLowerCase().includes(needle)),
        );
      }
      return { rows: page, nextCursor: null, unread: unread() };
    },
    async markRead(ids, read = true) {
      rows = rows.map((row) => (ids.includes(row.id) ? { ...row, read } : row));
    },
    async markAllRead() {
      rows = rows.map((row) => ({ ...row, read: true }));
    },
    async remove(ids) {
      rows = rows.filter((row) => !ids.includes(row.id));
    },
    async removeAll() {
      rows = [];
    },
    async settings() {
      return settings;
    },
    async updateSetting({ kind, channel, enabled }) {
      settings = settings.map((row) =>
        row.kind === kind
          ? { ...row, channels: row.channels.map((entry) => (entry.name === channel ? { ...entry, enabled } : entry)) }
          : row,
      );
    },
    async boardMutes() {
      return mutes;
    },
    async setBoardMute(input) {
      mutes =
        input.muteState === 'NOT_MUTED'
          ? mutes.filter((entry) => entry.boardId !== input.boardId)
          : [...mutes.filter((entry) => entry.boardId !== input.boardId), input];
    },
    async inboxFeed(input) {
      let page = inbox;
      if (input.boardIds && input.boardIds.length > 0) {
        page = page.filter((row) => input.boardIds?.includes(row.boardId));
      }
      if (input.unreadOnly) page = page.filter((row) => !row.read);
      if (input.tab !== 'all_updates' && input.tab !== 'mentioned') page = [];
      return { rows: page, nextCursor: null };
    },
    async inboxFacets(): Promise<InboxFacets> {
      const byBoard = new Map<string, { name: string; count: number }>();
      for (const row of inbox) {
        if (row.read) continue;
        const entry = byBoard.get(row.boardId) ?? { name: row.boardName ?? '', count: 0 };
        entry.count += 1;
        byBoard.set(row.boardId, entry);
      }
      const boards = [...byBoard.entries()].map(([boardId, entry]) => ({ boardId, ...entry }));
      return { total: boards.reduce((sum, entry) => sum + entry.count, 0), boards };
    },
    async inboxMarkRead(updateIds) {
      inbox = inbox.map((row) => (updateIds.includes(row.id) ? { ...row, read: true } : row));
    },
    async inboxMarkAllRead(boardIds) {
      inbox = inbox.map((row) =>
        !boardIds || boardIds.includes(row.boardId) ? { ...row, read: true } : row,
      );
    },
  };
}
